import { useParams, Navigate } from "react-router-dom";
import projectsJSON from "../../data/projects.json";
import { useState, useEffect, lazy, Suspense } from "react";

const Show = () => {
  const [project, setProject] = useState(null);
  const { slug } = useParams();

  useEffect(() => {
    const found = projectsJSON.find((p) => p.slug === slug);
    setProject(found);
  }, [slug]);

  if (project === null) {
    return (
      <div className="flex justify-center items-center h-screen">
        <h2 className="text-lg font-semibold animate-pulse text-secondary">
          Loading...
        </h2>
      </div>
    );
  }

  let techBadges = (project.technologies || []).map((technology, i) => {
    return (
      <div className="badge badge-outline bg-secondary" key={i}>
        {technology}
      </div>
    );
  });

  if (project === undefined) {
    return <Navigate to={`/projects/${slug}`} />;
  }

  // Only lazy-load the demo when we know which demo to import
  let Demo = null;
  if (project && project.demo) {
    Demo = lazy(() => import(`./demos/${project.demo}/App`));
  }

  return (
    <>
    <div className="breadcrumbs text-sm ml-2">
        <ul>
          <li>
            <a href="/" className="link link-hover">
              Home
            </a>
          </li>
          <li>
            <a href="/projects" className="link link-hover">
              Projects
            </a>
          </li>
          <li className="text-neutral">{slug}</li>
        </ul>
      </div>
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Hero / Overview */}
      <section className="space-y-4">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">{project.title}</h1>
        <p className="text-lg text-gray-700">{project.description || 'Project description to be added.'}</p>
        <div className="flex flex-wrap gap-3 mt-2">{techBadges}</div>
      </section>

      {/* Case study sections */}
      <section className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <article className="card bg-base-100 shadow-sm p-6">
            <h2 className="text-xl font-bold mb-2">Problem</h2>
            <p className="text-gray-700">{project.problem || 'Describe the problem you solved, target users, and constraints. (placeholder)'}</p>
          </article>

          <article className="card bg-base-100 shadow-sm p-6">
            <h2 className="text-xl font-bold mb-2">Solution</h2>
            <p className="text-gray-700">{project.solution || 'Summarise the solution, major features, and interaction highlights. (placeholder)'}</p>
          </article>

          <article className="card bg-base-100 shadow-sm p-6">
            <h2 className="text-xl font-bold mb-2">Impact</h2>
            <p className="text-gray-700">{project.impact || 'Share outcomes, metrics, or learnings. (placeholder)'}</p>
          </article>
        </div>

        <aside className="space-y-4">
          <div className="card bg-base-200 p-4">
            <h3 className="font-semibold">Role</h3>
            <p className="text-sm text-gray-700">{project.role || 'UX/UI, Frontend (placeholder)'}</p>
          </div>
          <div className="card bg-base-200 p-4">
            <h3 className="font-semibold">Timeline</h3>
            <p className="text-sm text-gray-700">{project.date || 'Date not set'}</p>
          </div>
          <div className="card bg-base-200 p-4">
            <h3 className="font-semibold">Tools</h3>
            <p className="text-sm text-gray-700">{(project.technologies || []).join(', ') || 'TBD'}</p>
          </div>
        </aside>
      </section>

      {/* Gallery / Demo */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold">Gallery / Demo</h2>
        {project.images && project.images.length ? (
          <div className="grid md:grid-cols-2 gap-4">
            {project.images.map((img, idx) => (
              <img key={idx} loading="lazy" src={require(`../../images/${img.url}`)} alt={img.caption} className="w-full rounded-lg shadow" />
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-neutral-100 p-12 text-center text-gray-600">No images available — add screenshots or mockups.</div>
        )}
      </section>

      {/* Demo embed if available */}
      {project.demo ? (
        <section>
          <h2 className="text-2xl font-bold mb-4">Interactive Demo</h2>
          <Suspense fallback={<div className="text-center text-secondary animate-pulse">Loading demo...</div>}>
            <Demo />
          </Suspense>
        </section>
      ) : null}
    </div>
    </>
  );
};

export default Show;
